import { Box, Text } from '@chakra-ui/react'
import { ContentNode, ContentNodeUtils, ObjectContentNode } from '@helo/cms'
import { Assert, If, Value, pipe } from '@helo/utils'

import { Pill } from '../../components/Pill/Pill'
import { Skeleton } from '../../components/SkeletonLoader'
import { TypeRenderer } from '../../datatypes/TypeRenderer'
import { createWidget } from '../widget-utils'

export interface RelatedShipmentsWidgetProps {
  contentNode: ContentNode
  isLoading: boolean
}

const headerTagPillStyles = {
  border: '1px solid',
  borderColor: 'presets.neutral.60',
  padding: '0 8px',
  size: 'sm' as const,
}

const shipmentLinkStyles = {
  fontSize: '12px',
  fontWeight: '600',
}

/** Container Details related shipments pill — mirrors PillWidget header tag styling. */
export const RelatedShipmentsWidget = ({
  contentNode,
  isLoading,
  ...props
}: RelatedShipmentsWidgetProps) => {
  if (isLoading) {
    return <Skeleton w="100px" h="20px" />
  }

  const items = pipe(
    contentNode,
    Assert.orThrow(ContentNodeUtils.isKind('FIELD')),
    ContentNodeUtils.getOfType(),
    Assert.orThrow(ContentNodeUtils.isKind('LIST')),
    ContentNodeUtils.getItems(),
  ) as ObjectContentNode[]
  const itemsLength = pipe(
    items,
    If.isElse(
      Value.isNullable,
      () => 0,
      items => items.length,
    ),
  )
  if (itemsLength === 0) {
    return null
  }

  return (
    <Pill {...headerTagPillStyles} {...props}>
      <Text
        color="presets.neutral.80"
        fontSize="12px"
        fontWeight="500"
        mr="8px"
      >
        {contentNode.state?.label}
      </Text>
      {items.map((item, index) => (
        <Box key={index} display="flex" alignItems="center">
          <TypeRenderer
            contentNode={{
              ...item.fields.find(field => field.key === 'shipmentDocumentId')
                ?.ofType,
              state: {
                ...item.fields.find(field => field.key === 'shipmentDocumentId')
                  ?.ofType.state,
                styles: shipmentLinkStyles,
              },
            }}
          />
          {index !== itemsLength - 1 && (
            <Box
              height="14px"
              width="1px"
              backgroundColor="presets.neutral.30"
              mx="4px"
            />
          )}
        </Box>
      ))}
    </Pill>
  )
}

export const RelatedShipmentsWidgetDefinition = createWidget(
  RelatedShipmentsWidget,
  {
    type: 'RelatedShipments',
    slots: [],
    isDataDriven: true,
  },
)
